import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { rfqAPI, clientAPI } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import { t } from '@/i18n/translations';
import { formatApiError } from '@/lib/apiError';
import { toast } from '@/components/ui/sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label, RequiredLabel } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

export default function RFQCreatePage() {
  const { lang } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    client_id: '',
    part_reference: '',
    part_designation: '',
    project_name: '',
    plan_number: '',
    plan_revision: '',
    plan_revision_date: '',
    deadline: '',
    annual_volume: '',
    commercial_context: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    clientAPI.list().then((r) => setClients(r.data)).catch(() => {});
  }, []);

  const set = (field) => (eOrValue) => {
    const value = typeof eOrValue === 'string' ? eOrValue : eOrValue.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.client_id ||
      !form.part_reference ||
      !form.part_designation ||
      !form.project_name ||
      !form.plan_number ||
      !form.plan_revision ||
      !form.plan_revision_date
    ) {
      toast.error(t('fill_required_fields', lang));
      return;
    }
    setLoading(true);
    try {
      const payload = {
        client_id: parseInt(form.client_id, 10),
        part_reference: form.part_reference,
        part_designation: form.part_designation,
        project_name: form.project_name,
        plan_number: form.plan_number,
        plan_revision: form.plan_revision,
        plan_revision_date: new Date(form.plan_revision_date).toISOString(),
        commercial_context: form.commercial_context,
        annual_volume: form.annual_volume ? parseInt(form.annual_volume, 10) : null,
        deadline: form.deadline ? new Date(form.deadline).toISOString() : null,
      };
      const res = await rfqAPI.create(payload);
      toast.success(t('rfq_created', lang));
      navigate(`/rfqs/${res.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err, t('rfq_create_error', lang)));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <PageHeader kicker={t('rfqs', lang)} title={t('new_rfq', lang)} />
      <form onSubmit={handleSubmit} className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>{t('rfq_data', lang)}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <RequiredLabel>{t('client', lang)}</RequiredLabel>
                <Select value={form.client_id} onValueChange={set('client_id')}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('select_client', lang)} />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.name} {c.code ? `(${c.code})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <RequiredLabel htmlFor="project-name">{t('project_name', lang)}</RequiredLabel>
                <Input
                  id="project-name"
                  value={form.project_name}
                  onChange={set('project_name')}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-1.5">
                <RequiredLabel htmlFor="plan-number">{t('plan_number', lang)}</RequiredLabel>
                <Input
                  id="plan-number"
                  value={form.plan_number}
                  onChange={set('plan_number')}
                />
              </div>
              <div className="space-y-1.5">
                <RequiredLabel htmlFor="plan-revision">{t('plan_revision', lang)}</RequiredLabel>
                <Input
                  id="plan-revision"
                  value={form.plan_revision}
                  onChange={set('plan_revision')}
                />
              </div>
              <div className="space-y-1.5">
                <RequiredLabel htmlFor="plan-revision-date">{t('plan_revision_date', lang)}</RequiredLabel>
                <Input
                  id="plan-revision-date"
                  type="date"
                  value={form.plan_revision_date}
                  onChange={set('plan_revision_date')}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <RequiredLabel htmlFor="part-ref">{t('part_ref', lang)}</RequiredLabel>
                <Input
                  id="part-ref"
                  value={form.part_reference}
                  onChange={set('part_reference')}
                  placeholder={t('part_ref_placeholder', lang)}
                />
              </div>
              <div className="space-y-1.5">
                <RequiredLabel htmlFor="designation">{t('part_designation', lang)}</RequiredLabel>
                <Input
                  id="designation"
                  value={form.part_designation}
                  onChange={set('part_designation')}
                  placeholder={t('part_designation_placeholder', lang)}
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="deadline">{t('deadline', lang)}</Label>
                <Input
                  id="deadline"
                  type="date"
                  value={form.deadline}
                  onChange={set('deadline')}
                />
                <p className="text-xs text-muted-foreground">{t('required_for_submit', lang)}</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="volume">{t('annual_volume', lang)}</Label>
                <Input
                  id="volume"
                  type="number"
                  min={0}
                  value={form.annual_volume}
                  onChange={set('annual_volume')}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="context">{t('commercial_context', lang)}</Label>
              <Textarea
                id="context"
                value={form.commercial_context}
                onChange={set('commercial_context')}
                placeholder={t('context_placeholder', lang)}
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-2">
          <Button type="submit" disabled={loading}>
            {loading ? t('creating_wait', lang) : `${t('create', lang)} RFQ`}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate('/rfqs')}>
            {t('cancel', lang)}
          </Button>
        </div>
      </form>
    </div>
  );
}
